/**
 * The query Rocky sends over the AGUI side-channel, and what the panel does with it.
 *
 * The spec IS a `ListRelevantPipelines` request, minus the three things only the
 * panel can know. Deriving it from the generated input type rather than
 * re-declaring a parallel shape is the point: the Smithy model stays the single
 * authority, and adding or renaming an input becomes a compile error here instead
 * of a field the skill can emit and the panel silently ignores.
 *
 * What the panel still owns, and why none of it can be delegated to Rocky:
 *   - `assetIds` is INTERSECTED with the Motion's assets. The panel is scoped to
 *     one Motion; a spec naming an asset outside it would surface candidates that
 *     have nothing to do with the Motion being curated.
 *   - defaults and paging (`maxResults`, `nextToken`, the relevance floor).
 *   - clamping values that would otherwise come back as a 400 the user cannot act
 *     on, since the spec crosses a model boundary.
 *
 * `describeSpec` / `removeToken` exist for a rule this feature holds itself to:
 * anything Rocky sets must be visible to the user AND removable by hand. A filter
 * they can neither see nor clear is worse than one they cannot set.
 */

import type { ListRelevantPipelinesInput } from '@amzn/ivthub-client';

/** A request with the panel-owned fields removed. Every member is optional. */
export type RelevanceQuerySpec = Omit<ListRelevantPipelinesInput, 'maxResults' | 'nextToken'>;

/** Server bounds, mirrored so a bad spec is corrected rather than rejected. */
const SEARCH_MIN = 2;
const SEARCH_MAX = 200;
const SCORE_MIN = 1;
const SCORE_MAX = 5;

/**
 * One active condition, as a dismissible token.
 *
 * Two kinds because they are cleared differently: a top-level field is deleted,
 * a filter condition is spliced out of its group.
 */
export type SpecToken =
  | { kind: 'field'; field: keyof RelevanceQuerySpec; label: string }
  | { kind: 'condition'; group: number; condition: number; label: string };

export interface RequestContext {
  /** The Motion's assets. A spec may narrow this set, never widen it. */
  motionAssetIds: string[];
  defaultMinRelevance: number;
  maxResults: number;
  nextToken?: string;
}

/**
 * The floor the query will ACTUALLY run with.
 *
 * Exported because the panel displays this value — in the segmented control and
 * in the empty state — and displaying the raw spec instead would let the UI claim
 * a floor of 9 while the request sent 5, and leave the control with no option
 * selected. One function, so display and request cannot disagree.
 */
export function resolveMinRelevance(spec: RelevanceQuerySpec, fallback: number): number {
  const v = spec.minRelevanceScore;
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.min(SCORE_MAX, Math.max(SCORE_MIN, Math.trunc(v)));
}

/**
 * The search term that will ACTUALLY be sent, or undefined.
 *
 * Same reason: a one-character term is below the server's minimum and is dropped
 * from the request, so showing a dismissible token for it would offer the user a
 * chip that does nothing.
 */
export function resolveSearchText(spec: RelevanceQuerySpec): string | undefined {
  const term = spec.searchText?.trim();
  if (!term || term.length < SEARCH_MIN) return undefined;
  return term.slice(0, SEARCH_MAX);
}

/**
 * The assets the query will ACTUALLY run against.
 *
 * An EMPTY result is meaningful and must never be sent: the server reads an empty
 * `assetIds` as "no asset restriction" rather than "no matching asset", so it
 * would answer with every opportunity in the caller's permitted industries. The
 * intersection exists to stop a spec from reaching past the Motion, and sending
 * its empty case would produce the widest possible query in exactly the situation
 * it guards against — silently, since rows come back and nothing errors.
 *
 * Callers must therefore branch on an empty result instead of forwarding it.
 * `toListRequest` refuses it outright; the panel shows an empty state naming the
 * reason, which is the only place that can explain it to the user.
 *
 * An empty `motionAssetIds` means the Motion's assets are not known yet, so there
 * is nothing to intersect against and the spec's own list stands.
 */
export function resolveAssetIds(spec: RelevanceQuerySpec, motionAssetIds: string[]): string[] {
  if (!spec.assetIds?.length) return motionAssetIds;
  if (!motionAssetIds.length) return spec.assetIds as string[];
  return motionAssetIds.filter((a) => spec.assetIds!.includes(a));
}

/**
 * Whether a condition says enough to be both applied and shown.
 *
 * Every member of the generated condition type is optional, so a partial one type-
 * checks. This is the single predicate for "does this count", used by `pruneTree` on
 * the way to the server and by `describeSpec` on the way to the screen — the two
 * must agree or the result is a filter that is applied without appearing anywhere.
 */
function isUsableCondition<T extends { field?: string; operator?: string }>(
  c: T,
): c is T & { field: string; operator: string } {
  return !!c.field && !!c.operator;
}

/**
 * The filter tree that will ACTUALLY be sent, or undefined if it constrains nothing.
 *
 * Prunes at both levels, because emptiness at either one is invisible from the other:
 * a group whose own `conditions` are empty still leaves `groups.length` at 1, and a
 * condition missing `field` or `operator` still leaves `conditions.length` at 1. Both
 * survive a length check while rendering no token, which makes them filters the user
 * can neither see nor clear — the one thing this module says it will not produce.
 *
 * The partial condition is the likelier of the two here: the spec is authored by an
 * LLM emitting free-form JSON, and every member of the modelled type is optional, so
 * nothing upstream of this rejects it. A spec arriving on the side-channel also never
 * passes through `removeToken`, so the tree has to be pruned on the way out and not
 * only on edit.
 */
function pruneTree(
  tree: RelevanceQuerySpec['propertyFilter'],
): RelevanceQuerySpec['propertyFilter'] | undefined {
  const groups = (tree?.groups ?? [])
    .map((g) => ({ ...g, conditions: (g.conditions ?? []).filter(isUsableCondition) }))
    .filter((g) => g.conditions.length > 0);
  return groups.length ? { ...tree!, groups } : undefined;
}

/**
 * Merge the spec with what the panel owns.
 *
 * Every paging call must pass the SAME spec: the cursor carries a fingerprint of
 * the filter set it was issued under, and the server rejects a mismatch rather
 * than silently re-paging.
 *
 * Throws when the spec's assets and the Motion's have nothing in common. A throw
 * rather than a comment because the alternative is not a wrong label but a query
 * spanning every asset the user can see; a caller that forgets to check gets a
 * visible failure instead of plausible-looking rows from other Motions.
 */
export function toListRequest(
  spec: RelevanceQuerySpec,
  ctx: RequestContext,
): ListRelevantPipelinesInput {
  const narrowed = resolveAssetIds(spec, ctx.motionAssetIds);
  if (!narrowed.length) {
    throw new Error(
      'no asset in common between the query and the Motion; the caller must handle this ' +
        'rather than send an empty assetIds, which the server reads as "any asset"',
    );
  }

  const req: ListRelevantPipelinesInput = {
    ...spec,
    assetIds: narrowed,
    minRelevanceScore: resolveMinRelevance(spec, ctx.defaultMinRelevance),
    maxResults: ctx.maxResults,
  };

  // Trimmed and bounded rather than passed through. A one-character term picked
  // out of a chat message is a 400, and the user has no way to connect that
  // failure to what they typed.
  const term = resolveSearchText(spec);
  if (term) req.searchText = term;
  else delete req.searchText;

  // Absent already means false server-side. Sending an explicit false only adds a
  // field to the page token's fingerprint for no change in behaviour.
  if (!spec.includeTagged) delete req.includeTagged;
  if (!spec.includeTerminalStage) delete req.includeTerminalStage;

  // An empty tree is not the same as no tree: a tree with nothing in it is legal on
  // the wire but describes a filter nobody asked for.
  const tree = pruneTree(spec.propertyFilter);
  if (tree) req.propertyFilter = tree;
  else delete req.propertyFilter;

  if (ctx.nextToken) req.nextToken = ctx.nextToken;
  return req;
}

/** Human label for one filter condition. */
function conditionLabel(field: string, operator: string, values: readonly string[]): string {
  const op =
    operator === 'in' ? '' : operator === 'notIn' ? 'not ' : `${{ gte: '≥', lte: '≤', gt: '>', lt: '<' }[operator] ?? operator} `;
  return `${field.toLowerCase().replace(/_/g, ' ')} ${op}${values.join(', ')}`.trim();
}

/**
 * The active conditions, for rendering as dismissible tokens.
 *
 * `minRelevanceScore` is deliberately absent: it always has a value, so it is a
 * control rather than a condition, and a removable token would imply it can be
 * switched off when it can only be changed.
 */
export function describeSpec(spec: RelevanceQuerySpec, motionAssetIds: string[] = []): SpecToken[] {
  const t: SpecToken[] = [];
  // The term that will be SENT, so a sub-minimum one shows no chip rather than a
  // chip with no effect.
  const term = resolveSearchText(spec);
  if (term) t.push({ kind: 'field', field: 'searchText', label: `text: “${term}”` });
  if (spec.assetIds?.length) {
    // The assets that SURVIVE the intersection, not the ones asked for. Naming an
    // asset outside the Motion drops it, and a token listing it would credit the
    // query with a narrowing it never applied.
    const kept = resolveAssetIds(spec, motionAssetIds);
    t.push({
      kind: 'field',
      field: 'assetIds',
      label: kept.length
        ? `assets: ${kept.join(', ')}`
        : 'assets: none of the requested ones are in this Motion',
    });
  }
  if (spec.useCases?.length) {
    t.push({ kind: 'field', field: 'useCases', label: `use cases: ${spec.useCases.join(', ')}` });
  }
  if (spec.opptyIds?.length) {
    t.push({ kind: 'field', field: 'opptyIds', label: `${spec.opptyIds.length} listed opportunities` });
  }
  // Only the widened case is worth saying — false is the server default.
  if (spec.includeTagged) {
    t.push({ kind: 'field', field: 'includeTagged', label: 'including already-tagged' });
  }
  if (spec.includeTerminalStage) {
    t.push({ kind: 'field', field: 'includeTerminalStage', label: 'including closed deals' });
  }
  spec.propertyFilter?.groups?.forEach((g, gi) => {
    g.conditions?.forEach((c, ci) => {
      // Same predicate the request is pruned with, so a condition is either both
      // sent and shown or neither.
      if (!isUsableCondition(c)) return;
      t.push({
        kind: 'condition',
        group: gi,
        condition: ci,
        label: conditionLabel(c.field, c.operator, c.values ?? []),
      });
    });
  });
  return t;
}

/** Remove one token's condition, returning a new spec. */
export function removeToken(spec: RelevanceQuerySpec, token: SpecToken): RelevanceQuerySpec {
  if (token.kind === 'field') {
    const next = { ...spec };
    delete next[token.field];
    return next;
  }
  const groups = (spec.propertyFilter?.groups ?? []).map((g, gi) =>
    gi !== token.group
      ? g
      : { ...g, conditions: (g.conditions ?? []).filter((_, ci) => ci !== token.condition) },
  );
  // A group emptied by the removal goes too, through the same prune the request
  // uses: it would otherwise keep counting as an active filter with nothing in it,
  // and one of the two paths dropping it while the other kept it is how "shown" and
  // "sent" drift apart in the first place.
  const next = { ...spec };
  const kept = pruneTree({ ...spec.propertyFilter!, groups });
  if (kept) next.propertyFilter = kept;
  else delete next.propertyFilter;
  return next;
}

/**
 * Stable key for "is this the same spec". Used to decide when an incoming
 * snapshot replaces the spec the user has been editing; comparing by object
 * identity would discard their edits on every unrelated store update.
 */
export function specKey(spec: RelevanceQuerySpec | undefined): string {
  const s = (spec ?? {}) as Record<string, unknown>;
  return JSON.stringify(
    Object.keys(s)
      // Absent and present-but-cleared are the same spec. Clearing a token can
      // leave an undefined key behind, and without this that would read as a
      // change and reset the user's other edits.
      .filter((k) => s[k] != null)
      .sort()
      .map((k) => [k, s[k]]),
  );
}
